import { useMemo, useState } from 'react';
import { Expand, Image as ImageIcon } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

type AppScreenshot = {
  id?: string;
  label?: string;
  imageUrl: string;
};

export default function AppScreenshotsGallery({
  title,
  screenshots,
}: {
  title: string;
  screenshots: AppScreenshot[];
}) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  const safeScreenshots = useMemo(
    () => screenshots.filter((screenshot) => Boolean(screenshot.imageUrl.trim())),
    [screenshots]
  );

  if (safeScreenshots.length === 0) {
    return null;
  }

  const activeScreenshot = safeScreenshots[selectedIndex] ?? safeScreenshots[0];

  return (
    <section aria-labelledby="app-screenshots-heading" className="space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h2 id="app-screenshots-heading" className="text-lg font-semibold text-foreground">
            App screenshots
          </h2>
          <p className="text-sm text-muted-foreground">Preview how this MapleOS app looks inside the live product.</p>
        </div>
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background/80 px-3 py-2 text-sm font-medium text-foreground transition-colors hover:border-primary/40 hover:text-primary"
        >
          <Expand className="h-4 w-4" />
          Expand
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          className="group relative overflow-hidden rounded-2xl border border-border/60 bg-background/80 text-left"
        >
          <img
            src={activeScreenshot.imageUrl}
            alt={activeScreenshot.label || `${title} screenshot`}
            className="aspect-[16/10] h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.01]"
            loading="lazy"
          />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 via-black/15 to-transparent p-4">
            <div className="flex items-center gap-2 text-sm text-white/80">
              <ImageIcon className="h-4 w-4" />
              <span>{activeScreenshot.label || 'MapleOS app view'}</span>
            </div>
          </div>
        </button>

        <div className="grid grid-cols-3 gap-3 lg:grid-cols-1">
          {safeScreenshots.map((screenshot, index) => {
            const isActive = index === selectedIndex;

            return (
              <button
                key={screenshot.id || `${screenshot.imageUrl}-${index}`}
                type="button"
                onClick={() => setSelectedIndex(index)}
                className={`overflow-hidden rounded-2xl border bg-background/80 text-left transition-all ${
                  isActive ? 'border-primary/50 ring-1 ring-primary/40' : 'border-border/60 hover:border-primary/30'
                }`}
              >
                <img
                  src={screenshot.imageUrl}
                  alt={screenshot.label || `${title} screenshot ${index + 1}`}
                  className="aspect-[4/3] w-full object-cover"
                  loading="lazy"
                />
              </button>
            );
          })}
        </div>
      </div>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="w-[92vw] max-w-4xl border-border bg-background p-0 text-foreground shadow-2xl">
          <DialogHeader className="border-b border-border px-5 py-3">
            <DialogTitle>{title} screenshots</DialogTitle>
            <DialogDescription>Published MapleOS screenshots for this app.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 p-5">
            <div className="overflow-hidden rounded-2xl border border-border/60 bg-card/60">
              <img
                src={activeScreenshot.imageUrl}
                alt={activeScreenshot.label || `${title} screenshot`}
                className="max-h-[56vh] w-full object-contain"
              />
            </div>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">

              {safeScreenshots.map((screenshot, index) => {
                const isActive = index === selectedIndex;

                return (
                  <button
                    key={screenshot.id || `${screenshot.imageUrl}-dialog-${index}`}
                    type="button"
                    onClick={() => setSelectedIndex(index)}
                    className={`overflow-hidden rounded-xl border bg-card/60 transition-all ${
                      isActive ? 'border-primary/50 ring-1 ring-primary/40' : 'border-border/60 hover:border-primary/30'
                    }`}
                  >
                    <img
                      src={screenshot.imageUrl}
                      alt={screenshot.label || `${title} screenshot ${index + 1}`}
                      className="aspect-[4/3] w-full object-cover"
                    />
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
